package com.schoolconnect.app.feature.attendance

import com.schoolconnect.app.datasource.network.request.QuickMarkExceptionRequest
import com.schoolconnect.app.datasource.network.request.QuickMarkRequest
import com.schoolconnect.app.designsystem.currentAppDateLabel
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.compareBySmartText
import com.schoolconnect.app.repository.attendance.AttendanceInsightsRepository
import com.schoolconnect.app.repository.attendance.AttendanceListRepository
import com.schoolconnect.app.datasource.network.response.TeacherAttendanceInsightStudent
import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.async
import kotlinx.coroutines.withTimeout
import kotlinx.coroutines.TimeoutCancellationException
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class AttendanceViewModel(
    private val roleCode: String = "",
    private val schoolId: String = UserSessionStore.getHomeRoleSchoolId()
) {
    private companion object {
        const val TAG = "[DATA_RETRIEVE_SC] AttendanceViewModel"
        const val QUICK_MARK_REQUEST_TIMEOUT_MS = 20_000L
    }
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var loadJob: Job? = null
    private var insightsJob: Job? = null
    private var quickMarkRosterJob: Job? = null
    private val _uiState = MutableStateFlow(AttendanceUiState(isLoading = true))
    val uiState: StateFlow<AttendanceUiState> = _uiState.asStateFlow()
    private val isSchoolAdmin = roleCode.trim() == SchoolKeys.V_ROLE_SCHOOL_ADMIN

    init {
        _uiState.update { it.copy(filters = it.filters.copy(date = currentAppDateLabel()), classFilterLabel = if (isSchoolAdmin) "All Classes" else "All My Classes") }
        loadTeacherAttendanceData()
    }

    fun onEvent(event: AttendanceUiEvent) {
        when (event) {
            AttendanceUiEvent.BackClicked -> Unit
            AttendanceUiEvent.RefreshClicked -> loadTeacherAttendanceData(forceRemote = true)
            is AttendanceUiEvent.SearchChanged -> {
                _uiState.update { it.copy(searchQuery = event.query, pageIndex = 0) }
                loadTeacherAttendanceData(debounceMs = 250)
            }
            is AttendanceUiEvent.FiltersChanged -> {
                _uiState.update { it.copy(filters = event.filters, pageIndex = 0) }
                loadTeacherAttendanceData()
            }
            AttendanceUiEvent.FiltersReset -> {
                _uiState.update {
                    it.copy(
                        filters = AttendanceFilterValues(date = currentAppDateLabel()),
                        searchQuery = "",
                        pageIndex = 0
                    )
                }
                loadTeacherAttendanceData()
            }
            is AttendanceUiEvent.PageChanged -> {
                _uiState.update { it.copy(pageIndex = event.pageIndex.coerceAtLeast(0)) }
                loadTeacherAttendanceData()
            }
            is AttendanceUiEvent.PageSizeChanged -> {
                _uiState.update { it.copy(pageIndex = 0, pageSize = event.pageSize.coerceIn(PaginationPolicy.PAGE_SIZE_OPTIONS.first(), PaginationPolicy.PAGE_SIZE_OPTIONS.last())) }
                loadTeacherAttendanceData()
            }
            is AttendanceUiEvent.TabChanged -> {
                _uiState.update { it.copy(selectedTab = event.tab, pageIndex = 0) }
                loadTeacherAttendanceData()
            }
            is AttendanceUiEvent.SortChanged -> {
                val ascending = if (_uiState.value.sortField == event.field) !_uiState.value.sortAscending else true
                _uiState.update { it.copy(sortField = event.field, sortAscending = ascending, pageIndex = 0) }
                loadTeacherAttendanceData()
            }
            is AttendanceUiEvent.StatusChanged -> Unit
            AttendanceUiEvent.QuickMarkClicked -> {
                quickMarkRosterJob?.cancel()
                _uiState.update {
                    it.copy(
                        quickMarkRosterRows = emptyList(),
                        isQuickMarkRosterLoading = false,
                        isQuickMarkAttendanceLoading = false,
                        quickMarkRosterError = ""
                    )
                }
            }
            is AttendanceUiEvent.QuickMarkContextChanged -> {
                loadQuickMarkRoster(event.classSectionId, event.attendanceDate)
            }
            is AttendanceUiEvent.AttendanceEdited -> editAttendance(event)
            AttendanceUiEvent.AttendanceEditAcknowledged -> _uiState.update { it.copy(attendanceEditCompleted = false) }
            is AttendanceUiEvent.QuickMarkSaved -> quickMarkAttendance(event.submission)
            AttendanceUiEvent.QuickMarkSaveAcknowledged -> _uiState.update { it.copy(quickMarkSaveCompleted = false) }
        }
    }

    private fun loadQuickMarkRoster(classSectionId: String, displayDate: String) {
        quickMarkRosterJob?.cancel()
        val targetDate = parseDisplayDate(displayDate)
        val dateError = attendanceDateValidationError(displayDate, currentAppDateLabel())
        if (classSectionId.isBlank() || targetDate == null || dateError.isNotBlank()) {
            _uiState.update {
                it.copy(
                    quickMarkRosterRows = emptyList(),
                    isQuickMarkRosterLoading = false,
                    isQuickMarkAttendanceLoading = false,
                    quickMarkRosterError = when {
                        classSectionId.isBlank() -> "Select a class to load its students."
                        dateError.isNotBlank() -> dateError
                        else -> "Enter a valid attendance date to load existing records."
                    }
                )
            }
            return
        }
        _uiState.update {
            it.copy(
                quickMarkRosterRows = emptyList(),
                isQuickMarkRosterLoading = true,
                isQuickMarkAttendanceLoading = true,
                quickMarkRosterError = ""
            )
        }
        quickMarkRosterJob = scope.launch {
            try {
                val response = withTimeout(QUICK_MARK_REQUEST_TIMEOUT_MS) {
                    AttendanceListRepository.loadQuickMarkData(classSectionId, targetDate).getOrThrow()
                }
                val classLabel = _uiState.value.availableClasses.firstOrNull { it.id == classSectionId }?.label.orEmpty().ifBlank { "—" }
                val rows = checkNotNull(response.students) { "Quick Mark data did not include the student list. Please retry." }
                    .map { student ->
                        val studentId = student.studentId?.takeIf(String::isNotBlank)
                            ?: error("Quick Mark data included a student without an ID. Please retry.")
                        val attendance = student.attendance
                        AttendanceRowUi(
                            key = studentId,
                            studentId = studentId,
                            rollNumber = student.rollNumber.orEmpty().ifBlank { "—" },
                            studentName = student.studentName.orEmpty().ifBlank { "—" },
                            classLabel = classLabel,
                            status = attendance?.status.orEmpty().ifBlank { "—" },
                            remarks = attendance?.remarks.orEmpty(),
                            attendanceId = attendance?.attendanceId.orEmpty()
                        )
                    }
                _uiState.update {
                    it.copy(
                        quickMarkRosterRows = rows,
                        isQuickMarkRosterLoading = false,
                        isQuickMarkAttendanceLoading = false,
                        quickMarkRosterError = ""
                    )
                }
            } catch (error: TimeoutCancellationException) {
                _uiState.update {
                    it.copy(
                        quickMarkRosterRows = emptyList(),
                        isQuickMarkRosterLoading = false,
                        isQuickMarkAttendanceLoading = false,
                        quickMarkRosterError = "Timed out loading Quick Mark students. Please retry."
                    )
                }
            } catch (error: CancellationException) {
                throw error
            } catch (error: Exception) {
                scLog("$TAG quickMark data failure classSectionId=$classSectionId targetDate=$targetDate", error)
                _uiState.update {
                    it.copy(
                        quickMarkRosterRows = emptyList(),
                        isQuickMarkRosterLoading = false,
                        isQuickMarkAttendanceLoading = false,
                        quickMarkRosterError = error.message.orEmpty().ifBlank {
                            "Unable to load Quick Mark students and attendance. Please retry."
                        }
                    )
                }
            }
        }
    }

    private fun loadTeacherAttendanceData(forceRemote: Boolean = false, debounceMs: Long = 0) {
        loadJob?.cancel()
        insightsJob?.cancel()
        loadJob = scope.launch {
            if (debounceMs > 0) delay(debounceMs)
            val state = _uiState.value
            val sortBy = state.sortField.apiField
            val order = if (state.sortAscending) "asc" else "desc"
            _uiState.update { it.copy(isLoading = true, isInsightsLoading = true, errorMessage = "") }
            val selectedDate = parseDisplayDate(state.filters.date)
            val (startDate, endDate) = if (state.selectedTab == AttendanceTab.INSIGHTS) {
                insightDateRange(state.filters.timePeriod)
            } else {
                selectedDate to selectedDate
            }
            val selectedStatus = state.filters.status.takeIf { state.selectedTab == AttendanceTab.MARK_ATTENDANCE }
            val selectedBand = state.filters.performance.takeIf { state.selectedTab == AttendanceTab.INSIGHTS }
            val insights = async {
                // Teachers and school admins use the same teacher-scoped
                // attendance insights endpoint. Authorization determines the
                // rows returned for the current session.
                AttendanceInsightsRepository.loadPage(
                    startDate = startDate,
                    endDate = endDate,
                    band = selectedBand?.ifBlank { null },
                    classSectionId = state.filters.classSectionId.takeIf { it.isNotBlank() },
                    pageIndex = state.pageIndex,
                    pageSize = state.pageSize,
                    sortBy = sortBy,
                    order = order,
                    query = state.searchQuery,
                    status = selectedStatus,
                    forceRemote = forceRemote,
                    refreshScope = scope
                )
            }
            val statistics = async { AttendanceInsightsRepository.loadStatistics(startDate, endDate, forceRemote, scope) }
            val insightResult = insights.await()
            val statisticsResult = statistics.await()
            val rosterRows = insightResult.getOrNull()?.students.orEmpty().map(::toRosterRow)
            insightResult.fold(
                onSuccess = { response ->
                    val insightRows = response.students.map(::toInsightRow)
                    val rows = response.students.map { student ->
                        toAttendanceRow(student)
                    }
                    val stats = statisticsResult.getOrNull()
                    _uiState.update { current ->
                        current.copy(
                            rows = rows,
                            attendanceRosterRows = rosterRows,
                            totalCount = response.total ?: stats?.totalStudents ?: rows.size,
                            insightRows = insightRows,
                            insightTotal = response.total ?: insightRows.size,
                            insightStudentCount = stats?.totalStudents ?: response.totalStudents ?: response.total ?: insightRows.size,
                            insightAverageValue = (stats?.overallAverageAttendancePercentage ?: response.classAverage ?: 0.0).toInt(),
                            insightGoodCount = stats?.goodPerformanceCount ?: response.goodPerformanceCount ?: 0,
                            insightAveragePerformanceCount = stats?.averagePerformanceCount ?: response.averagePerformanceCount ?: 0,
                            insightNeedsAttentionCount = stats?.needsAttentionCount ?: response.needsAttentionCount ?: 0,
                            availableClasses = stats?.classSections.orEmpty().mapNotNull { item ->
                                val id = item.classSectionId?.trim().orEmpty()
                                val label = listOfNotNull(item.className, item.sectionName).joinToString("-")
                                if (id.isBlank() || label.isBlank()) null else AttendanceClassOption(id, label)
                            }.distinctBy { it.id }.sortedWith(compareBySmartText { option -> option.label }),
                            classFilterLabel = if (isSchoolAdmin) "All Classes" else "All My Classes",
                            isLoading = false,
                            isInsightsLoading = false,
                            errorMessage = ""
                        )
                    }
                },
                onFailure = { throwable ->
                    scLog("$TAG loadTeacherAttendanceData failure", throwable)
                    _uiState.update {
                        it.copy(
                            rows = emptyList(),
                            attendanceRosterRows = rosterRows,
                            insightRows = emptyList(),
                            totalCount = 0,
                            insightTotal = 0,
                            insightStudentCount = 0,
                            insightAverageValue = 0,
                            insightGoodCount = 0,
                            insightAveragePerformanceCount = 0,
                            insightNeedsAttentionCount = 0,
                            isLoading = false,
                            isInsightsLoading = false,
                            errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load attendance." }
                        )
                    }
                }
            )
        }
    }

    private fun quickMarkAttendance(submission: QuickMarkSubmission) {
        val classId = submission.classSectionId.trim()
        val attendanceDate = parseDisplayDate(submission.attendanceDate)
        val dateError = attendanceDateValidationError(submission.attendanceDate, currentAppDateLabel())
        if (classId.isBlank() || attendanceDate == null || dateError.isNotBlank()) {
            _uiState.update { it.copy(saveErrorMessage = dateError.ifBlank { "Select a class and valid attendance date." }) }
            return
        }
        _uiState.update { it.copy(isQuickMarkSaving = true, quickMarkSaveCompleted = false, saveErrorMessage = "") }
        loadJob?.cancel()
        loadJob = scope.launch {
            AttendanceInsightsRepository.quickMarkAttendance(
                QuickMarkRequest(
                    classId = classId,
                    attendanceDate = attendanceDate,
                    defaultStatus = com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.serverCode("ATTENDANCE_STATUS", "Present"),
                    exceptions = submission.exceptions.mapNotNull { exception ->
                        val studentId = exception.studentId.trim()
                        if (studentId.isBlank()) null else QuickMarkExceptionRequest(
                            studentId = studentId,
                            status = com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.serverCode("ATTENDANCE_STATUS", exception.status),
                            remarks = exception.remarks?.trim()?.takeIf { it.isNotBlank() }
                        )
                    }
                )
            ).fold(
                onSuccess = {
                    _uiState.update { it.copy(isQuickMarkSaving = false, quickMarkSaveCompleted = true, saveErrorMessage = "") }
                    loadTeacherAttendanceData(forceRemote = true)
                },
                onFailure = { throwable ->
                    scLog("$TAG quickMarkAttendance failure", throwable)
                    _uiState.update { it.copy(isQuickMarkSaving = false, quickMarkSaveCompleted = false, saveErrorMessage = throwable.message.orEmpty().ifBlank { "Unable to save attendance." }) }
                }
            )
        }
    }

    private fun editAttendance(event: AttendanceUiEvent.AttendanceEdited) {
        val row = _uiState.value.rows.firstOrNull { it.key == event.rowKey }
        val studentId = row?.studentId?.trim().orEmpty()
        val attendanceDate = parseDisplayDate(event.date)
        val dateError = attendanceDateValidationError(event.date, currentAppDateLabel())
        if (studentId.isBlank() || attendanceDate == null || dateError.isNotBlank()) {
            _uiState.update {
                it.copy(saveErrorMessage = dateError.ifBlank { "Select a valid student and attendance date." })
            }
            return
        }

        _uiState.update { it.copy(isAttendanceSaving = true, attendanceEditCompleted = false, saveErrorMessage = "") }
        loadJob?.cancel()
        loadJob = scope.launch {
            val attendanceId = row?.attendanceId?.trim().orEmpty()
            val result = if (attendanceId.isNotBlank()) {
                AttendanceListRepository.updateAttendance(
                    attendanceId = attendanceId,
                    status = event.status.toApiAttendanceStatus(),
                    remarks = event.remarks
                )
            } else {
                val classSectionId = _uiState.value.availableClasses
                    .firstOrNull { option -> option.label == row?.classLabel }
                    ?.id
                    .orEmpty()
                if (classSectionId.isBlank()) {
                    Result.failure(IllegalStateException("Unable to resolve the student's class section for this attendance date."))
                } else {
                    AttendanceListRepository.createAttendance(
                        studentId = studentId,
                        classSectionId = classSectionId,
                        targetDate = attendanceDate,
                        status = event.status.toApiAttendanceStatus(),
                        remarks = event.remarks
                    )
                }
            }
            result.fold(
                onSuccess = {
                    _uiState.update {
                        it.copy(
                            isAttendanceSaving = false,
                            attendanceEditCompleted = true,
                            saveErrorMessage = "",
                            filters = it.filters.copy(date = event.date.trim()),
                            pageIndex = 0
                        )
                    }
                    loadTeacherAttendanceData(forceRemote = true)
                },
                onFailure = { throwable ->
                    scLog("$TAG editAttendance failure rowKey=${event.rowKey}", throwable)
                    _uiState.update {
                        it.copy(
                            isAttendanceSaving = false,
                            attendanceEditCompleted = false,
                            saveErrorMessage = throwable.message.orEmpty().ifBlank { "Unable to update attendance." }
                        )
                    }
                }
            )
        }
    }

    private fun String.toApiAttendanceStatus(): String =
        com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.serverCode("ATTENDANCE_STATUS", trim())

    private fun toAttendanceRow(student: TeacherAttendanceInsightStudent): AttendanceRowUi {
        return AttendanceRowUi(
            key = student.studentId.orEmpty().ifBlank { student.rollNumber.orEmpty() },
            studentId = student.studentId.orEmpty(),
            rollNumber = student.rollNumber.orEmpty().ifBlank { student.admissionNumber.orEmpty().ifBlank { "—" } },
            studentName = student.studentName.orEmpty().ifBlank { "—" },
            classLabel = listOfNotNull(student.className, student.sectionName).joinToString("-").ifBlank { "—" },
            status = student.attendance?.status?.trim()?.takeIf { it.isNotBlank() } ?: "—",
            remarks = student.attendance?.remarks?.trim().orEmpty(),
            attendanceId = student.attendance?.attendanceId.orEmpty()
        )
    }

    private fun toRosterRow(student: TeacherAttendanceInsightStudent): AttendanceRowUi {
        return AttendanceRowUi(
            key = student.studentId.orEmpty().ifBlank {
                listOf(student.rollNumber, student.studentName, student.className, student.sectionName)
                    .joinToString("|") { it.orEmpty() }
            },
            studentId = student.studentId.orEmpty(),
            rollNumber = student.rollNumber.orEmpty().ifBlank { "—" },
            studentName = student.studentName.orEmpty().ifBlank { "—" },
            classLabel = listOfNotNull(student.className, student.sectionName).joinToString("-").ifBlank { "—" },
            status = "—",
            remarks = ""
        )
    }

    private fun toInsightRow(student: TeacherAttendanceInsightStudent): AttendanceInsightRowUi {
        val percentage = student.attendancePercentage ?: 0.0
        return AttendanceInsightRowUi(
            key = student.studentId.orEmpty().ifBlank { student.rollNumber.orEmpty().ifBlank { student.admissionNumber.orEmpty() } },
            studentId = student.studentId.orEmpty(),
            rollNumber = student.rollNumber.orEmpty().ifBlank { student.admissionNumber.orEmpty().ifBlank { "—" } },
            studentName = student.studentName.orEmpty().ifBlank { "—" },
            classLabel = listOfNotNull(student.className, student.sectionName).joinToString("-").ifBlank { "—" },
            attendancePercentage = percentage.toInt(),
            totalDays = student.totalDays ?: 0,
            presentDays = student.presentDays ?: 0,
            absentDays = student.absentDays ?: 0,
            lateDays = student.lateDays ?: 0,
            excusedDays = student.excusedDays ?: 0,
            performance = student.performanceBand.orEmpty().ifBlank { "—" }
        )
    }

    private fun parseDisplayDate(value: String): String? {
        val parts = value.trim().split(" ")
        if (parts.size != 3) return null
        val month = mapOf("JAN" to "01", "FEB" to "02", "MAR" to "03", "APR" to "04", "MAY" to "05", "JUN" to "06", "JUL" to "07", "AUG" to "08", "SEP" to "09", "OCT" to "10", "NOV" to "11", "DEC" to "12")[parts[1].uppercase()] ?: return null
        return "${parts[2].padStart(4, '0')}-$month-${parts[0].padStart(2, '0')}"
    }

    private fun insightDateRange(timePeriod: String): Pair<String?, String?> {
        val today = parseDisplayDate(currentAppDateLabel()) ?: return null to null
        val start = when (timePeriod) {
            "Last 7 Days" -> shiftIsoDate(today, 6)
            "Last 30 Days" -> shiftIsoDate(today, 29)
            "This Month" -> today.take(7) + "-01"
            else -> return null to null
        }
        return start to today
    }

    private fun shiftIsoDate(date: String, daysBack: Int): String {
        val parts = date.split("-")
        var year = parts[0].toInt()
        var month = parts[1].toInt()
        var day = parts[2].toInt() - daysBack
        while (day < 1) {
            month -= 1
            if (month == 0) {
                month = 12
                year -= 1
            }
            val leapYear = year % 4 == 0 && (year % 100 != 0 || year % 400 == 0)
            val daysInMonth = when (month) {
                2 -> if (leapYear) 29 else 28
                4, 6, 9, 11 -> 30
                else -> 31
            }
            day += daysInMonth
        }
        return "${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
    }

    fun clear() {
        loadJob?.cancel()
        insightsJob?.cancel()
        scope.cancel()
    }
}
